import {
  Checkbox,
  FormControl,
  useToast
} from '@chakra-ui/react';
import { useState } from 'react';
import FormModal from '../../../components/FormModal';
import FormField from '../../../components/fields/FormField';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { SportType } from '../../../models/court/dto/court.dto';
import { CreateOrganizationSportGameCapacityOptionDto } from '../../../models/organization/dto/organization-sport-game-capacity-option.dto';
import { useCreateOrganizationGameCapacityOption } from '../../organization/hooks/useOrganizationGameCapacityOptions';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { SPORT_OPTIONS } from './GameCapacityOptionsList';
import { buildCapacityWriteWarning } from '../utils/capacity-write-warnings';
import {
  MAX_BOOKING_CAPACITY,
  MIN_BOOKING_CAPACITY,
  sportTypeToCapacity,
} from '../../../utils/sport-capacity';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}


export default function CreateGameCapacityOptionForm({ isOpen, onClose }: Props) {
  const { selectedOrganization } = useSelectedOrganization();
  const [sport, setSport] = useState<SportType>(SportType.PADEL);
  const [capacity, setCapacity] = useState<number>(sportTypeToCapacity(SportType.PADEL));
  // Once the operator types a capacity, changing the sport must not overwrite
  // it. Until then the field follows the sport, so picking TENNIS pre-fills 2
  // rather than leaving a padel-shaped 4 sitting in a two-player sport's form.
  const [capacityEdited, setCapacityEdited] = useState<boolean>(false);
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const toast = useToast();

  const handleSportChange = (nextSport: SportType) => {
    setSport(nextSport);
    if (!capacityEdited) {
      setCapacity(sportTypeToCapacity(nextSport));
    }
  };

  const { mutateAsync: createCapacityOption, isLoading } = useCreateOrganizationGameCapacityOption(
    selectedOrganization?.id || '',
    (result) => {
      // A plain success toast was the whole defect on this rail. Configuring the
      // FIRST capacity option for a sport is not additive: it moves the
      // organization off the unconfigured fallback, and the capacity installed
      // pre-picker builds send is not carried over — so those builds start
      // failing at payment, after a quote that succeeded, while the operator is
      // told the save worked. The same save can also leave a capacity bookable
      // that no court prices. Warning rather than error: the option was created
      // and is correct, and pricing it afterwards is the natural order of
      // operations.
      const warning = buildCapacityWriteWarning(result);
      if (warning) {
        toast({
          title: translate('game_capacity_options.capacity_option_created'),
          description: warning,
          status: 'warning',
          duration: 10000,
          isClosable: true,
          position: 'top-right',
        });
        handleClose();
        return;
      }
      toast({
        title: translate('game_capacity_options.capacity_option_created'),
        status: 'success',
        duration: 3000,
        position: 'top-right',
      });
      handleClose();
    }
  );

  const handleClose = () => {
    // Reset form when closing
    setSport(SportType.PADEL);
    setCapacity(sportTypeToCapacity(SportType.PADEL));
    setCapacityEdited(false);
    setIsDefault(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedOrganization?.id) return;

    const formData: Omit<CreateOrganizationSportGameCapacityOptionDto, 'organizationId'> = {
      sport,
      capacity,
      isDefault,
    };

    try {
      await createCapacityOption(formData);
    } catch (error: any) {
      toast({
        title: translate('game_capacity_options.error'),
        description: error?.message || translate('game_capacity_options.error_creating'),
        status: 'error',
        duration: 5000,
        position: 'top-right',
      });
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={translate('game_capacity_options.create_capacity_option')}
      onSubmit={handleSubmit}
      submitLabel={translate('game_capacity_options.save')}
      cancelLabel={translate('game_capacity_options.cancel')}
      isSubmitting={isLoading}
      isSubmitDisabled={!selectedOrganization?.id}
    >
      <FormStack>
        <FormField
          type="select"
          label={translate('game_capacity_options.sport')}
          value={sport}
          onChange={(value) => handleSportChange(value as SportType)}
          options={SPORT_OPTIONS}
        />

        <FormField
          type="number"
          label={translate('game_capacity_options.capacity')}
          value={capacity}
          onChange={(value) => {
            setCapacityEdited(true);
            setCapacity(value || 1);
          }}
          min={MIN_BOOKING_CAPACITY}
          // The money paths reject anything above this, so an option saved
          // beyond it would be offered in the picker and then 400 at payment.
          // The field used to allow up to 50.
          max={MAX_BOOKING_CAPACITY}
        />

        <FormControl>
          <Checkbox
            isChecked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          >
            {translate('game_capacity_options.set_as_default')}
          </Checkbox>
        </FormControl>
      </FormStack>
    </FormModal>
  );
}
